import type { Breadcrumb } from './ontology/types';

/** Maximum number of breadcrumbs retained; older entries are dropped (ring buffer size). */
const BREADCRUMB_CAPACITY = 20;

/** Callback invoked when the breadcrumb store changes, used by external store subscribers. */
type BreadcrumbSubscriber = () => void;

/** Internal source of truth for the captured breadcrumbs. */
let breadcrumbs: Breadcrumb[] = [];

/**
 * Cached, externally exposed snapshot of {@link breadcrumbs}. Its reference changes
 * only when the contents change, so `useSyncExternalStore` consumers stay stable
 * and never enter an infinite render loop; copying also shields the internal array
 * from external mutation.
 */
let breadcrumbSnapshot: Breadcrumb[] = [];

/** Registered subscribers notified after every breadcrumb store mutation. */
const subscribers = new Set<BreadcrumbSubscriber>();

/** Notifies all registered subscribers that the breadcrumb store changed. */
function notifySubscribers() {
  subscribers.forEach((subscriber) => {
    subscriber();
  });
}

/**
 * Appends a breadcrumb (stamping it with the current time), trims the buffer to
 * {@link BREADCRUMB_CAPACITY}, refreshes the cached snapshot, and notifies subscribers.
 */
export function pushBreadcrumb(input: Omit<Breadcrumb, 'ts'>): void {
  const nextBreadcrumb: Breadcrumb = {
    ...input,
    ts: Date.now(),
  };

  breadcrumbs = [...breadcrumbs, nextBreadcrumb].slice(-BREADCRUMB_CAPACITY);
  breadcrumbSnapshot = [...breadcrumbs];
  notifySubscribers();
}

/**
 * Returns the cached breadcrumb snapshot for `useSyncExternalStore`. The reference is
 * stable between mutations, so callers receive a new array only when the contents change.
 */
export function getBreadcrumbSnapshot(): Breadcrumb[] {
  return breadcrumbSnapshot;
}

/** Subscribes to breadcrumb store changes and returns an unsubscribe function. */
export function subscribeToBreadcrumbs(subscriber: BreadcrumbSubscriber): () => void {
  subscribers.add(subscriber);

  return () => {
    subscribers.delete(subscriber);
  };
}

/** Clears the breadcrumb store; intended for resetting state between tests. */
export function resetBreadcrumbStoreForTest(): void {
  breadcrumbs = [];
  breadcrumbSnapshot = [];
  notifySubscribers();
}
